import React from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { ExternalLink, RefreshCw } from "lucide-react";
import EmotionRadar from "./EmotionRadar";
import { PolarityBadge, WarStanceBadge } from "./StanceBadge";
import {
  EMOTIONS, EMOTION_LABELS_TR, EMOTION_COLORS, EMOTION_EMOJI, formatDateTR,
} from "../lib/constants";

export default function StatementDetail({ open, onClose, country, statement, onRefresh, refreshing }) {
  if (!statement) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-[#0A0A0A] border-white/10 rounded-sm text-white" data-testid="statement-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <img src={country?.flag_url} alt="" className="h-6 w-9 object-cover rounded-sm border border-white/10" />
            <div>
              <DialogTitle className="font-heading text-lg">{country?.name_tr}</DialogTitle>
              <DialogDescription className="data-label mt-0.5">{country?.ministry_tr}</DialogDescription>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <PolarityBadge polarity={statement.polarity} />
              <WarStanceBadge stance={statement.war_stance} />
              {onRefresh && (
                <Button
                  data-testid="refresh-country-btn"
                  size="sm" variant="outline"
                  className="rounded-sm border-white/15 bg-transparent text-white hover:bg-white/5"
                  onClick={() => onRefresh(country.code)} disabled={refreshing}
                >
                  <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
                  Yenile
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh] pr-2">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <div className="data-label mb-1">Başlık</div>
              <div className="text-white text-base font-heading mb-3" data-testid="statement-headline">
                {statement.headline_tr || "—"}
              </div>
              <div className="data-label mb-1">Özet</div>
              <p className="text-sm text-gray-200 leading-relaxed" data-testid="statement-summary">
                {statement.summary_tr}
              </p>
              {statement.tone_tr && (
                <div className="mt-3">
                  <div className="data-label mb-1">Ton</div>
                  <div className="text-sm text-orange-300 font-mono">{statement.tone_tr}</div>
                </div>
              )}
              {statement.keywords_tr?.length > 0 && (
                <div className="mt-3">
                  <div className="data-label mb-1">Anahtar Kelimeler</div>
                  <div className="flex flex-wrap gap-1.5">
                    {statement.keywords_tr.map((k, i) => (
                      <span key={`${k}-${i}`} className="px-2 py-0.5 bg-white/5 border border-white/10 rounded-sm text-[11px] font-mono text-gray-300">
                        {k}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-4 flex flex-col gap-1 text-xs font-mono text-gray-400">
                <div><span className="text-gray-500">Tarih:</span> {formatDateTR(statement.statement_date)}</div>
                <div><span className="text-gray-500">Kaynak:</span> {statement.source}</div>
                {statement.source_url && (
                  <a href={statement.source_url} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300">
                    Kaynağa git <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                {statement.synthetic && (
                  <div className="text-amber-400/70 mt-1">Bu kayıt temsili başlangıç verisidir.</div>
                )}
              </div>
            </div>
            <div>
              <div className="data-label mb-1">Duygu Radarı</div>
              <EmotionRadar emotions={statement.emotions} color="#EF4444" height={260} />
            </div>
          </div>

          <div className="mt-6">
            <div className="data-label mb-2">Duygu Boyutları ve Gerekçeler</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {EMOTIONS.map((e) => (
                <Card key={e} className="rounded-sm bg-[#141414] border-white/10 p-3" data-testid={`emotion-card-${e}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-base">{EMOTION_EMOJI[e]}</span>
                      <span className="font-heading text-sm text-white">{EMOTION_LABELS_TR[e]}</span>
                    </div>
                    <div className="font-mono font-bold text-sm" style={{ color: EMOTION_COLORS[e] }}>
                      {statement.emotions?.[e] ?? 0}
                    </div>
                  </div>
                  <div className="w-full bg-white/5 h-1 mt-2">
                    <div className="h-1" style={{
                      width: `${statement.emotions?.[e] ?? 0}%`, background: EMOTION_COLORS[e],
                    }} />
                  </div>
                  <p className="text-xs text-gray-300 mt-2 leading-snug">
                    {statement.justifications_tr?.[e] || "—"}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
